import {Inject, Injectable} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {DOCUMENT} from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class UiNotificationService {
  private readonly DEFAULT_ERROR_DURATION = 10000;
  private readonly DEFAULT_SUCCESS_DURATION = 3000;
  private activeNotifications: HTMLElement[] = [];
  private readonly NOTIFICATION_GAP = 10;
  private readonly NOTIFICATION_MIN_HEIGHT = 60;

  constructor(
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public showError(message: string, duration: number = this.DEFAULT_ERROR_DURATION): void {
    this.createNotification('error', message, duration);
  }

  public showSuccess(message: string, duration: number = this.DEFAULT_SUCCESS_DURATION): void {
    this.createNotification('success', message, duration);
  }

  public showInfo(message: string, duration: number = this.DEFAULT_SUCCESS_DURATION): void {
    this.createNotification('info', message, duration);
  }

  public showWarning(message: string, duration: number = this.DEFAULT_SUCCESS_DURATION): void {
    this.createNotification('warn', message, duration);
  }

  public handleHttpError(error: HttpErrorResponse): void {
    let errorMessage = 'Сталася помилка при виконанні запиту';

    if (error.status === 0) {
      errorMessage = `Проблема з'єднання з сервером або CORS. Перевірте підключення до інтернету і перезавантажте браузер`;
    } else if (error.status === 401) {
      errorMessage = 'Необхідна авторизація. Увійдіть у систему';
    } else if (error.status === 403) {
      errorMessage = 'Доступ заборонено. У вас немає прав для виконання цієї дії';
    } else if (error.status === 404) {
      errorMessage = 'Ресурс не знайдено';
    } else if (error.status >= 500) {
      errorMessage = 'Помилка сервера. Спробуйте пізніше';
    }

    if (error.error && error.error.message) {
      errorMessage = error.error.message;
    }

    this.showError(`${errorMessage}</br>${error.url}</br>${error.message}</br>${error.error.stack || ''}`);
  }

  public handleClientError(error: Error): void {
    this.showError(error.stack);
  }

  private positionNotification(notification: HTMLElement): void {
    let topPosition = 20;

    if (this.activeNotifications.length > 0) {
      let totalHeight = 0;

      this.activeNotifications.forEach(activeNotification => {
        const height = activeNotification.offsetHeight || this.NOTIFICATION_MIN_HEIGHT;
        totalHeight += height + this.NOTIFICATION_GAP;
      });
      topPosition = 20 + totalHeight;
    }

    notification.style.top = `${topPosition}px`;
    this.activeNotifications.push(notification);
    this.recalculatePositions();
  }

  private repositionNotifications(removedNotification: HTMLElement): void {
    const index = this.activeNotifications.indexOf(removedNotification);
    if (index !== -1) {
      this.activeNotifications.splice(index, 1);
      this.recalculatePositions();
    }
  }

  private recalculatePositions(): void {
    let currentTop = 20;
    this.activeNotifications.forEach((notification, index) => {
      notification.style.top = `${currentTop}px`;
      const height = notification.offsetHeight || this.NOTIFICATION_MIN_HEIGHT;
      currentTop += height + this.NOTIFICATION_GAP;
    });
  }

  private removeNotification(notification: HTMLElement): void {
    if (this.document.body.contains(notification)) {
      this.document.body.removeChild(notification);
      this.repositionNotifications(notification);
    }
  }

  private createNotification(type: 'error' | 'success' | 'info' | 'warn', message: string, duration: number): void {
    const notification = this.document.createElement('div');
    notification.innerHTML = message;
    notification.className = `ui-notification ui-notification-${type}`;

    notification.style.cssText = `
      position: fixed;
      right: 20px;
      padding: 15px 20px;
      border-radius: 4px;
      max-width: 750px;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      z-index: 9999;
      animation: fadeIn 0.3s ease-out forwards;
      opacity: 0;
      min-height: ${this.NOTIFICATION_MIN_HEIGHT - 30}px;
      box-sizing: border-box;
      display: block;
    `;

    this.positionNotification(notification);

    if (type === 'error') {
      notification.style.backgroundColor = '#f44336';
      notification.style.color = 'white';
    } else if (type === 'success') {
      notification.style.backgroundColor = '#4caf50';
      notification.style.color = 'white';
    } else if (type === 'warn') {
      notification.style.backgroundColor = '#ff9800';
      notification.style.color = 'white';
    } else {
      notification.style.backgroundColor = '#2196f3';
      notification.style.color = 'white';
    }

    const closeButton = this.document.createElement('span');
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = `
      position: absolute;
      top: 5px;
      right: 10px;
      cursor: pointer;
      font-size: 20px;
    `;
    closeButton.onclick = () => this.removeNotification(notification);
    notification.appendChild(closeButton);

    const style = this.document.createElement('style');
    style.innerHTML = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeOut {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(-20px); }
      }
    `;
    this.document.head.appendChild(style);
    this.document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = 'fadeOut 0.3s ease-in forwards';
      setTimeout(() => {
        this.removeNotification(notification);
      }, 300);
    }, duration);
  }
}
